// Wire contract for session expiration, shared by the API and the three frontends.

/**
 * Set on requests that are not the user doing something (periodic token refresh,
 * status reads, anything issued while the tab has seen no interaction). These do not
 * slide the idle window, which is what stops a forgotten open tab keeping a session
 * alive. Absent by default, so other API consumers keep the sliding behaviour.
 */
export const BACKGROUND_REQUEST_HEADER = 'x-dealstack-background';
export const BACKGROUND_REQUEST_HEADER_VALUE = '1';

/**
 * Sent on every authenticated response so the client never has to poll for the deadline.
 *
 * Durations in whole seconds, not instants. The client has to compare these against
 * something, and comparing a server instant against a device clock means any offset
 * between the two shows up as a wrong deadline — a device running an idle window fast
 * judged a freshly issued session already expired. A duration is anchored on arrival, so
 * only the local clock's *rate* matters, and rate error over half an hour is nothing.
 */
export const SESSION_IDLE_EXPIRES_IN_HEADER = 'x-dealstack-session-idle-expires-in';
export const SESSION_ABSOLUTE_EXPIRES_IN_HEADER = 'x-dealstack-session-absolute-expires-in';

/** Set on the 401 that ends a session, so the client can explain why. */
export const SESSION_END_REASON_HEADER = 'x-dealstack-session-end-reason';

/** Query param on the login URL, so the login page can explain what happened. */
export const SESSION_END_REASON_PARAM = 'sessionEnd';

export enum SessionEndReason {
	IDLE = 'idle',
	ABSOLUTE = 'absolute',
	/** Revoked, or no session on the server at all — the user did not ask for this. */
	ENDED = 'ended',
	/** The user chose to sign out, so the login page has nothing to explain. */
	SIGNED_OUT = 'signed-out',
}

export const parseSessionEndReason = (value: string | null): SessionEndReason | null =>
	Object.values(SessionEndReason).find((reason) => reason === value) ?? null;

/** Whole seconds until a deadline, floored at zero. */
export const secondsUntil = (deadline: Date, now: Date): number =>
	Math.max(0, Math.ceil((deadline.getTime() - now.getTime()) / 1000));

/** Response body of GET /auth/session. Durations in seconds, for the reason above. */
export interface SessionStatusPayload {
	idleExpiresInSeconds: number;
	absoluteExpiresInSeconds: number;
	idleWarningSeconds: number;
	idleTimeoutSeconds: number;
}
